
import { Goals, Users } from "@/api";
import GoalsChart from "@/components/goals/GoalsChart.vue";
import GoalsDiagram from "@/components/goals/GoalsDiagram.vue";
import Spinner from "@/components/shared/Spinner.vue";
import moment from "moment";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { Component, Vue } from "vue-property-decorator";
import { Getter } from "vuex-class";

const namespace = "auth";

@Component({
  components: {
    ValidationProvider,
    ValidationObserver,
    GoalsDiagram,
    GoalsChart,
    Spinner
  }
})
export default class GoalsPage extends Vue {
  userGoals: any = [];
  isAdmin = false;
  /** Typed, because a bare [] infers never[] and the build rejects it. */
  categories: string[][] = [];
  seriesData: number[] = [];
  users: any = [];
  selectedUser = 0;
  rating: any = {
    rate: 0
  };
  companyTotal: any = {};
  loading = false;
  /** Which year the monthly chart covers; the years the data actually has. */
  selectedYear: number = new Date().getFullYear();
  years: number[] = [new Date().getFullYear()];
  /**
   * The company-wide figures, which the page needs on load and again
   * whenever Company Total is picked. It was written out twice, once in
   * each caller, and the two copies had already started to drift.
   */
  private applyCompanyTotals(goalsTotal: any): void {
    const data = goalsTotal?.data || {};
    this.rememberYears(data);

    this.users = [
      { title: "Company Total", key: 0 },
      ...(data.goals || []).map((user: any) => ({
        title: user.userName,
        key: user.userId
      }))
    ];
    this.userGoals = [
      {
        progress: data.totalCompany?.totalProgress,
        amount: data.totalCompany?.totalAmount
      }
    ];
    this.applyMonthSeries(data.statisticMonth);
  }

  /**
   * The years the server says it holds figures for. Kept as the picker's
   * list so it can never offer a year with nothing behind it.
   */
  private rememberYears(data: any): void {
    if (Array.isArray(data?.years) && data.years.length) {
      this.years = data.years;
      if (!this.years.includes(this.selectedYear)) {
        this.selectedYear = this.years[0];
      }
    }
  }

  /**
   * The month-by-month series both views draw.
   *
   * The server always wraps the list one level deeper than it looks, and the
   * company branch unwrapped it while the per-user branch did not — so a
   * selected user got a single bar labelled Invalid date with no value.
   * Accept either shape rather than relying on the caller to know.
   */
  private applyMonthSeries(months: any): void {
    let rows = Array.isArray(months) ? months : [];
    if (rows.length && Array.isArray(rows[0])) {
      rows = rows[0];
    }

    this.categories = rows.map((month: any) => [
      moment(month.date).format("MMM YY")
    ]);
    this.seriesData = rows.map((month: any) => Number(month[0]?.progress) || 0);
  }

  async updateStatisctics() {
    try {
      this.loading = true;
      this.categories = [];
      this.seriesData = [];
      this.userGoals = [];

      if (this.selectedUser) {
        // Three independent reads: asked for together rather than one
        // after the other, which was three round trips deep.
        const [goals, goalsByMonths, rating]: any[] = await Promise.all([
          Goals.getUserGoalsData(this.selectedUser),
          Goals.getTotalGoalsUser(this.selectedUser, this.selectedYear),
          Users.getSingleUserRating(this.selectedUser)
        ]);
        this.userGoals = goals?.data || [];
        this.rating = rating;
        this.rememberYears(goalsByMonths?.data);
        this.applyMonthSeries(goalsByMonths?.data?.statisticMonth);
      } else {
        const [goalsTotal, companyRating]: any[] = await Promise.all([
          Goals.getGoalsData(this.selectedYear),
          Goals.getRatingCompany()
        ]);
        this.rating.rate = +companyRating.rate;
        this.applyCompanyTotals(goalsTotal);
      }
    } catch (errors) {
      await this.$error(errors.errors);
    }
    this.loading = false;
  }

  async mounted() {
    this.loading = true;
    const currentUser: any = this.getUser;
    const isAdmin = Boolean(
      currentUser?.permissions?.some(
        (permission: string) => permission === "create_role"
      )
    );
    this.isAdmin = isAdmin;

    if (isAdmin) {
      try {
        // getCcAgents used to be fetched here and the result thrown away on
        // the very next line, which rebuilt the same list from goalsTotal.
        const [goalsTotal, companyRating]: any[] = await Promise.all([
          Goals.getGoalsData(this.selectedYear),
          Goals.getRatingCompany()
        ]);
        this.rating.rate = +(+companyRating.rate).toFixed(2);

        const usersMonth = goalsTotal?.data?.usersMonth || {};
        this.companyTotal = [
          {
            header: "",
            progress: usersMonth.new?.progress,
            amount: usersMonth.new?.amount,
            sumPrice: usersMonth.new?.price
          },
          {
            header: "Existing Customers Target",
            progress: usersMonth.exist?.progress,
            amount: usersMonth.exist?.amount,
            sumPrice: usersMonth.exist?.price
          }
        ];
        this.applyCompanyTotals(goalsTotal);
      } catch (errors) {
        await this.$error(errors.errors);
      }
    } else {
      this.selectedUser = currentUser.id;
      try {
        const goals: any = await Goals.getUserGoalsData(currentUser.id);
        this.userGoals = goals?.data || [];
      } catch (errors) {
        await this.$error(errors.errors);
      }
    }
    this.loading = false;
  }

  @Getter("getUser", { namespace })
  getUser!: object | null;
}
